<template>
  <div class="h-full flex flex-col">
    <!-- ヘッダー部 -->
    <div class="w-full bg-white border-b-[1px] border-gray200">
      <div class="flex w-full items-center h-[47px] pl-3 pr-5">
        <img
          src="@/assets/Icon_Left_grayV2.svg"
          class="w-6 h-6"
          @click="goToBackPage()"
        />
        <div
          class="ml-2 mr-6 w-full text-W7 text-[15px] leading-[21px] truncate"
        >
          {{ selectedSpot.name }}
        </div>
      </div>
    </div>
    <!-- Topに戻るボタン -->
    <ScrollTopButton
      targetId="spot-detail-contents"
      class="fixed bottom-[72px] right-5"
    />
    <div
      class="flex-1 border-t-[1px] border-gray300 overflow-y-auto overscroll-none"
      ref="scrollContainer"
      id="spot-detail-contents"
    >
      <PullToRefreshAndPagination
        @refresh="handleRefresh"
        @pagination="handlePagination"
        @scroll="handleScroll"
        ref="spotDetail"
      >
        <!-- マップ -->
        <div class="h-[193px] relative">
          <BaseMap
            ref="SpotDetailMap"
            :mapElementId="mapElementId"
            :isTouchMap="false"
            :copyrightBottomSpace="6"
            :defaultCenter="selectedSpotCoord"
            @finish-generate-map="moveMapCenterToSpot()"
          />
          <img
            src="@/assets/Icon_Map_Place.svg"
            class="absolute positionPin w-11 h-11 z-10 pointer-events-none"
          />
        </div>
        <!-- 地点情報・お気に入り・ルート検索 -->
        <div class="m-5">
          <div class="text-left">
            <!-- 地点名 -->
            <div class="flex w-full items-center mb-1">
              <img src="@/assets/Icon_Map_Place.svg" class="w-3 h-3 mr-2" />
              <div class="flex-1 text-W7 text-[15px] leading-[15px]">
                {{ selectedSpot.name }}
              </div>
            </div>
            <!-- 距離・住所 -->
            <div class="flex w-full items-center">
              <div class="w-3 mr-2" />
              <div
                class="flex-1 text-W4 text-[11px] leading-[11px] text-gray400"
              >
                {{ `${distanceText}${selectedSpot.address}` }}
              </div>
            </div>
          </div>
          <!-- お気に入り登録・ルート検索 -->
          <div class="flex mt-4">
            <TheButton
              buttonType="secondary"
              class="!p-4"
              :text="favoriteButton.text"
              :prevImgPath="favoriteButton.icon"
              @click="handleFavoriteSpotDataOperation()"
            />
            <TheButton
              buttonType="primary"
              text="ルート検索"
              class="ml-2"
              @click="searchRoute()"
            />
          </div>
        </div>
        <!-- みんなの投稿 -->
        <div class="h-[51px] bg-gray200 pl-5 flex items-center">
          <div class="text-W7 text-[15px] leading-[15px] text-left">
            みんなの投稿
          </div>
        </div>
        <!-- 検索結果 -->
        <div class="bg-gray200">
          <template v-if="isFetchFailed">
            <div>
              <div class="text-W4 text-gray400 text-[13px] leading-[23px]">
                投稿を取得できませんでした
              </div>
              <div class="my-4 flex justify-center">
                <img :src="errorSrc" class="h-[140px] w-[140px]" />
              </div>
              <TheButton
                text="再読み込み"
                class="m-auto w-[123px]"
                @click-button="initialCommentList()"
              />
            </div>
          </template>
          <template v-else>
            <div
              v-for="(comment, index) in commentList"
              :key="comment.commentId"
            >
              <div v-if="index !== 0" class="h-2 w-full bg-gray200" />
              <CommentCard
                :comment="comment"
                :isShowSpotInfo="false"
                @on-click-comment="goToCommentDetail(comment)"
              />
            </div>
          </template>
        </div>
      </PullToRefreshAndPagination>
      <div class="h-[80px] w-full bg-gray200" />
    </div>
    <!-- 登録件数上限通知用ポップアップ -->
    <Modal
      v-show="isMaximumFavoriteSpotsFlg"
      class="modal text-center"
      :isShowCloseButton="false"
      :isModalCenter="true"
      modalPaddingX="20px"
    >
      <div class="px-4 pt-9 pb-4">
        <div class="pb-3 text-W7 text-[17px] leading-[25.5px]">
          お気に入り登録数は最大{{ favoriteSpotMaxNum }}件です。
        </div>
        <div class="pb-5 text-W4 text-[15px] leading-[23px]">
          新しく登録したい場合は、登録済みのお気に入りを削除してください。
        </div>
        <div
          class="text-W7 the-button-default text-[15px] leading-[15px] py-4 mx-auto mb-5"
          @click="gotoFavoriteSpot()"
        >
          お気に入り地点を編集
        </div>
        <div
          class="text-W7 the-button-secondary text-[15px] leading-[15px] py-4 mx-auto"
          @click="isMaximumFavoriteSpotsFlg = false"
        >
          閉じる
        </div>
      </div>
    </Modal>
  </div>
</template>

<script>
import CommentCard from '@/components/organisms/dokoiku/CommentCard.vue'
import PullToRefreshAndPagination from '@/components/molecules/PullToRefreshAndPagination.vue'
import BaseMap from '@/components/atoms/BaseMap.vue'
import TheButton from '@/components/atoms/TheButton.vue'
import Modal from '@/components/Modal.vue'
import Util from '@/mixins/util'
import {goBackDokoiku, navigateTo, META_TAB} from '@/router'
import {getDistanceText, calcDistance} from '@/utils/utils'
import {getFavoriteSpot} from '@/utils/spotUtil'
import {addFavoriteSpotList, deleteFavoriteSpotList} from '@/utils/menuUtil'
import NativeUtil from '@/mixins/nativeUtil.js'
import ScrollTopButton from '@/components/atoms/ScrollTopButton.vue'

const SpotDetail = {
  name: 'SpotDetail',
  components: {
    CommentCard,
    PullToRefreshAndPagination,
    TheButton,
    Modal,
    BaseMap,
    ScrollTopButton,
  },
  data() {
    return {
      errorSrc: Util.methods.getErrorAssetsSrc(), // 検索失敗時の画像パス
      mapElementId: this.$config.MAP_SCRIPT_ELEMENT_ID.DOKOIKU_MAP,
      isMaximumFavoriteSpotsFlg: false, // お気に入り地点登録数が上限に達してい
      favoriteSpotMaxNum: this.$config.FAVORITE_SPOT_MAX_NUMBER, // お気に入り地点材大登録数
    }
  },
  computed: {
    /**
     * 選択中の地点情報
     */
    selectedSpot: {
      get() {
        return this.$store.state.DokoikuStore.spotDetail.selectedSpot
      },
    },
    /**
     * 選択中の地点座標
     */
    selectedSpotCoord: {
      get() {
        const coord = this.selectedSpot?.coord
        if (!coord?.lat || !coord?.lon) return
        return {lat: Number(coord.lat), lon: Number(coord.lon)}
      },
    },
    /**
     * タイムライン記事情報
     */
    commentList: {
      get() {
        return this.$store.state.DokoikuStore.spotDetail.commentList
      },
      set(value) {
        let newList = []
        /* eslint-disable-next-line no-magic-numbers*/
        if (this.offset === 0) {
          // 先頭ページ読み込みの場合、新しく置き換え
          newList = value
        } else {
          // 続きのページ読み込みの場合、タイムライン追加
          const currentCommentList =
            this.$store.state.DokoikuStore.spotDetail.commentList
          const tmpCommentList = structuredClone(currentCommentList)
          tmpCommentList.push(...value)
          newList = tmpCommentList
        }
        this.$store.commit('DokoikuStore/setSpotDetailInfo', {
          key: 'commentList',
          value: newList,
        })
      },
    },
    /**
     * ページネーション情報
     */
    offset: {
      get() {
        return this.$store.state.DokoikuStore.spotDetail.offset
      },
      set(value) {
        this.$store.commit('DokoikuStore/setSpotDetailInfo', {
          key: 'offset',
          value: value,
        })
      },
    },
    /**
     * タイムライン取得失敗有無
     */
    isFetchFailed: {
      get() {
        return this.$store.state.DokoikuStore.spotDetail.isFetchFailed
      },
      set(value) {
        this.$store.commit('DokoikuStore/setSpotDetailInfo', {
          key: 'isFetchFailed',
          value: value,
        })
      },
    },
    /**
     * スクロール位置
     */
    scrollPosition: {
      get() {
        return this.$store.state.DokoikuStore.spotDetail.scrollPosition
      },
      set(value) {
        this.$store.commit('DokoikuStore/setSpotDetailInfo', {
          key: 'scrollPosition',
          value: value,
        })
      },
    },
    /**
     * 現在地情報
     */
    currentPosition: {
      get() {
        return this.$store.state.currentPosition
      },
    },
    /**
     * 表示する距離文言
     * @returns 距離の文言
     */
    distanceText: {
      get() {
        const lat = this.selectedSpot?.coord?.lat
        const lon = this.selectedSpot?.coord?.lon
        if (!lat || !lon) return ''

        // 現在地と地点間の距離を算出(座標データが存在しない、不適切の場合は空文字が返却)
        const distanceM = calcDistance({lat, lon}, this.currentPosition)
        if (typeof distanceM == 'undefined') return ''

        // 表示用に加工して返却
        const showDistance = getDistanceText(distanceM)
        return showDistance
      },
    },
    /** 登録済お気に入り地点 */
    registeredFavoriteSpot: {
      get() {
        const spot = {
          spotName: this.selectedSpot.name,
          address: this.selectedSpot.address,
          coord: `${this.selectedSpot?.coord?.lat},${this.selectedSpot?.coord?.lon}`,
        }
        return getFavoriteSpot(
          spot,
          this.$store.getters['MenuStore/getFavoriteList']
        )
      },
    },
    /** 表示されている地点は既に登録済 */
    existFavoriteSpotInStorage: {
      get() {
        return typeof this.registeredFavoriteSpot !== 'undefined'
      },
    },
    /** お気に入り登録ボタン情報 */
    favoriteButton: {
      get() {
        return this.existFavoriteSpotInStorage
          ? {
              text: 'お気入り解除',
              icon: 'Icon_Favorite_On.svg',
            }
          : {
              text: 'お気に入り登録',
              icon: 'Icon_Favorite_Off.svg',
            }
      },
    },
  },
  mounted() {
    const el = document.getElementById('spot-detail-contents')
    el.addEventListener('scroll', this.$refs.spotDetail.handleScroll)
  },
  async activated() {
    const prevPageMeta = this.$route.params.prevMeta
    const isGoBack = this.$route.params.isGoBack || false
    // ドコイク機能の画面内遷移で、前に進む遷移を行った場合は情報の更新を行う
    if (!isGoBack && prevPageMeta.tab == META_TAB.DOKOIKU) {
      this.moveMapCenterToSpot()
      await this.initialCommentList()
    }
    // アクティブになったタイミングで、スクロール位置を元の状態に切り替える。
    this.$nextTick(() => {
      this.$refs.scrollContainer.scrollTop = this.scrollPosition
    })
  },
  methods: {
    /**
     * 地点にマップの中心をずらす
     */
    moveMapCenterToSpot() {
      if (!this.selectedSpotCoord) return
      this.$refs?.SpotDetailMap.setCenterAndZoomDefault(
        this.selectedSpotCoord.lat,
        this.selectedSpotCoord.lon
      )
    },
    /**
     * タイムライン記事更新処理
     * @param {Number} offset 次投稿データ開始位置
     * @returns {Array} 取得した記事情報
     */
    fetchCommentList(offset) {
      return new Promise((resolve, reject) => {
        this.$store.dispatch('DokoikuStore/fetchCommentListBySpotCode', {
          success: (result) => {
            resolve(result)
          },
          failed: () => {
            reject()
          },
          error: (e) => {
            reject(e)
          },
          spotCode: this.selectedSpot.spotCode,
          sortName: 'createAt',
          sortPattern: 'desc',
          offset: offset,
        })
      })
    },
    /**
     * 初回表示時のタイムライン記事更新処理
     */
    async initialCommentList() {
      this.$store.commit('startLoading')
      try {
        const {items} = await this.fetchCommentList(this.offset)
        /* eslint-disable-next-line no-magic-numbers*/
        if (items.length === 0) throw new Error()

        this.commentList = items
        this.offset = this.commentList.length
        this.isFetchFailed = false
      } catch (e) {
        // 取得失敗処理
        this.isFetchFailed = true
        this.commentList = []
        const status = e?.response?.status
        if (status === this.$config.API_STATUS_CODE_SERVICE_UNAVAILABLE) {
          // メンテナンスモードの場合、エラーをスローする
          throw e
        }
      } finally {
        this.$store.commit('endLoading')
      }
    },
    /**
     * リフレッシュ操作時処理
     * @param {Function} refreshDone 終了処理
     */
    async handleRefresh(refreshDone) {
      // ページを先頭に戻し、投稿一覧を更新
      this.offset = 0
      try {
        const {items} = await this.fetchCommentList(this.offset)
        /* eslint-disable-next-line no-magic-numbers*/
        if (items.length === 0) throw new Error()
        this.commentList = items
        this.offset = this.commentList.length
        this.isFetchFailed = false
      } catch (e) {
        // 取得失敗処理
        this.isFetchFailed = true
        this.commentList = []
        const status = e?.response?.status
        if (status === this.$config.API_STATUS_CODE_SERVICE_UNAVAILABLE) {
          // メンテナンスモードの場合、終了処理を行った上エラーをスローする
          refreshDone()
          throw e
        }
      }
      refreshDone()
    },
    /**
     * ページネーション操作時処理
     * @param {Function} paginationDone 終了処理 引数:スクロール対象要素ID
     */
    async handlePagination(paginationDone) {
      // ページを更新し、投稿一覧を追加
      const prevPage = structuredClone(this.offset)
      try {
        const {items} = await this.fetchCommentList(this.offset)
        /* eslint-disable-next-line no-magic-numbers*/
        if (items.length === 0) throw new Error()

        this.commentList = items
        this.offset = this.commentList.length
      } catch (e) {
        // 取得失敗処理
        // 元のページに戻す
        this.offset = prevPage
        const status = e?.response?.status
        if (status === this.$config.API_STATUS_CODE_SERVICE_UNAVAILABLE) {
          // メンテナンスモードの場合、終了処理を行った上エラーをスローする
          paginationDone('tag-search-result-contents')
          throw e
        }
      }
      paginationDone('tag-search-result-contents')
    },
    /**
     * タイムラインスクロール処理
     * @param {Number} scrollTop スクロール位置
     */
    handleScroll(scrollTop) {
      this.scrollPosition = scrollTop
    },
    /**
     * 前画面に遷移
     */
    goToBackPage() {
      goBackDokoiku(this.$route.name)
    },
    /**
     * コメント詳細画面へ遷移
     * @param {Object} comment 選択した記事
     */
    goToCommentDetail(comment) {
      this.$store.commit('DokoikuStore/initCommentDetailInfo')
      this.$store.commit('DokoikuStore/setCommentDetailInfo', {
        key: 'selectedComment',
        value: comment,
      })
      // 画面を離れる前にページ情報を保持
      this.$store.dispatch('PageControl/saveDokoikuHistoryPage', this.$route)
      navigateTo({name: this.$config.DISPLAY_DOKOIKU_COMMENT_DETAIL})
      NativeUtil.methods.sendViewDokoikuCommentPageLogEventForGA(
        comment.contributorId,
        comment.commentId,
        comment.title
      )
    },
    /**
     * ルートタブに切り替え、現在地から表示地点までのルート検索を行う
     */
    searchRoute() {
      /** 再検索に伴う初期化処理 */
      // 最適画面 - 検索状態の初期化
      this.$store.commit('updateSearchRoute', false)
      this.$store.commit('RouteStore/updateIsCompleteSearchRoute', false)

      // ルート描画初期化(ローディングなし)
      this.$store.commit('updateIsNoLoadingWhenDrawRoute', true)
      this.$store.commit('clearDrawRouteData')
      this.$store.commit('clearDrawRouteScript')

      // 検索条件の初期化
      this.$store.commit('initializeSearchConditions')

      /** 検索条件設定 */
      // 出発地点(現在地)
      this.$store.commit('updateSearchConditions', {
        key: this.$config.SPOT_TYPE_START,
        value: {name: '現在地', address: '', coord: ''},
      })

      // 到着地点(表示している地点)
      const goalSpot = structuredClone(this.selectedSpot)
      goalSpot.coord = `${goalSpot.coord.lat},${goalSpot.coord.lon}` // 検索するためには緯度経度を結合する必要があるため
      this.$store.commit('updateSearchConditions', {
        key: this.$config.SPOT_TYPE_GOAL,
        value: goalSpot,
      })
      // 時間(現在時刻/出発)
      this.$store.commit('updateSearchConditions', {
        key: 'timeType',
        value: {id: 1, name: '出発'},
      })
      this.$store.commit('updateSearchConditions', {
        key: 'targetTime',
        value: Util.methods.getNow(),
      })

      /** 遷移処理 */
      // 最適画面に遷移
      this.$store.commit('updateFooterTab', this.$config.FOOTER_ID_SPOT)
      this.$store.commit('updateCurrentPage', {
        key: 'dokoiku',
        value: this.$route.name,
      })
      navigateTo({name: this.$config.DISPLAY_ROUTE_BY_OPTIMAL})
      // ストレージのタブIDを更新
      Util.methods.setLocalStorage(
        this.$config.FOOTER_TAB,
        this.$config.FOOTER_ID_SPOT
      )
    },
    /**
     * お気に入り登録処理
     * 既に20件ある場合はモーダル表示
     */
    handleFavoriteSpotDataOperation() {
      // 複数のトーストオブジェクトを出さないため
      this.$snackbar.clear()

      /* お気に入り地点の削除/登録 */
      let commitData
      if (this.existFavoriteSpotInStorage) {
        // Storage,Storeにある地点を削除
        commitData = deleteFavoriteSpotList(
          this.registeredFavoriteSpot,
          this.$config.FAVORITE_SPOT
        )
        this.$store.commit('MenuStore/updateFavoriteSpots', commitData)
      } else {
        // 登録数が上限に達している
        if (
          this.favoriteSpotMaxNum <=
          this.$store.getters['MenuStore/getFavoriteList'].length
        ) {
          this.isMaximumFavoriteSpotsFlg = true
          return
        }

        // Storage,Storeに地点を登録
        const spot = {
          spotName: this.selectedSpot.name,
          address: this.selectedSpot.address,
          coord: `${this.selectedSpot.coord.lat},${this.selectedSpot.coord.lon}`,
          buildingName: '',
          category: this.selectedSpot.category,
        }

        const searchIdEmptyForFavoriteSpot = ''
        commitData = addFavoriteSpotList(
          spot,
          this.$config.FAVORITE_SPOT,
          searchIdEmptyForFavoriteSpot // 検索用IDは新規発行する
        ).commitData

        this.$store.commit('MenuStore/updateFavoriteSpots', commitData)

        // トースト展開
        this.openSuccessSnackbar()
      }
    },

    /** トースト展開 */
    openSuccessSnackbar() {
      const length = this.$store.getters['MenuStore/getFavoriteList'].length
      this.$snackbar.add({
        type: 'success',
        text: `お気に入りに追加しました！（${length}/${this.favoriteSpotMaxNum}件）`,
        icon: require('@/assets/Icon_Success.svg'), // 左側配置のアイコン
      })
    },

    /** お気に入り地点一覧画面へ遷移 */
    gotoFavoriteSpot() {
      this.isMaximumFavoriteSpotsFlg = false
      // 画面を離れる前にページ情報を保持
      this.$store.dispatch('PageControl/saveDokoikuHistoryPage', this.$route)
      // お気に入り一覧画面を表示
      navigateTo({name: this.$config.DISPLAY_FAVORITE_SPOT_LIST})
    },
  },
}
export default SpotDetail
</script>

<style scooped>
.positionPin {
  transform: translateY(-100%) translateX(-50%);
  top: calc(50% + 4px);
  left: calc(50%);
}
</style>
